<script setup>
/**
 * A thread's own page in the book (journal practice 2026-10-28, DESIGN §5):
 * the name (named from the seeker's words; tap it to rename in place), the
 * span, how the question changed (the first and the latest, side by side, no
 * commentary), then one step per reading, oldest first: the date, the
 * question, then "My hunch" and "What I took from it" only where the seeker
 * wrote them (journal threads left, 2026-10-28): never an empty label; the
 * step's ✎ menu adds a missing one in place. A reading done for someone shows
 * its question only. No verdicts, no scores, no Merge (the endpoint stays).
 * Tapping a step's question turns to that day.
 */
import { ref, computed } from 'vue'
import JournalInPlace from './JournalInPlace.vue'
import JournalAddOwn from './JournalAddOwn.vue'
import { threadSpan, stepDate, missingOwn, OWN_LABEL, OWN_CHANGE } from '@/utils/journal-practice'

const props = defineProps({
  thread: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  backLabel: { type: String, default: '' },
})
const emit = defineEmits(['back', 'goto', 'rename', 'line'])

const span = computed(() => threadSpan(props.thread))
const NOTE = 'Your words. The overnight writer never changes them.'
const PIECES = ['gut', 'landed']
const adding = ref('')   // `${readingId}:${piece}` while a line is being added
const stepId = (s) => s.readingId || s.key
const shows = (s, p) => !s.forName && (!!s[p] || adding.value === `${stepId(s)}:${p}`)
const saveStep = (s, piece) => (text) => emit('line', { key: s.key, readingId: s.readingId, piece, text })
</script>

<template>
  <div class="thv" data-testid="journal-thread">
    <div class="thh">
      <div class="krow">
        <span class="k">A thread · named from your words</span>
        <button type="button" class="bk" data-testid="journal-view-back" @click="emit('back')">‹ {{ backLabel || 'Back' }}</button>
      </div>
      <p v-if="loading && !thread" class="hidnote">Opening the thread…</p>
      <template v-if="thread">
        <h2><JournalInPlace :text="thread.name" size="title" :edited="thread.nameIsUser" note="Every day tagged with it follows." label="Rename this thread"
                            testid="journal-thread-name" @save="emit('rename', { id: thread.id, name: $event })" /></h2>
        <p class="meta">{{ span }}</p>
      </template>
    </div>

    <template v-if="thread">
      <div v-if="thread.arc" class="arc" data-testid="journal-thread-arc">
        <b>How the question changed</b>From <i>“{{ thread.arc.first }}”</i> to <i>“{{ thread.arc.latest }}”</i>
      </div>
      <ol class="steps">
        <li v-for="s in thread.steps" :key="stepId(s)" class="st" data-testid="journal-thread-step">
          <button type="button" class="sd" :aria-label="`Turn to ${stepDate(s.key).month} ${stepDate(s.key).day}`" @click="emit('goto', s.key)">{{ stepDate(s.key).day }}<small>{{ stepDate(s.key).month }}</small></button>
          <div>
            <div class="qrow">
              <button type="button" class="q" data-testid="journal-step-open" @click="emit('goto', s.key)">{{ s.question }}</button>
              <JournalAddOwn :pieces="missingOwn(s)" testid="journal-step-add" @add="adding = `${stepId(s)}:${$event}`" />
            </div>
            <template v-for="p in PIECES" :key="p">
              <div v-if="shows(s, p)" :class="p === 'gut' ? 'gut' : 'sh'"><b>{{ OWN_LABEL[p] }}</b><JournalInPlace :text="s[p]" quote size="step" :note="NOTE" :edited="p === 'gut' ? s.gutEdited : s.landedEdited"
                :label="OWN_CHANGE[p]" :autostart="!s[p]" :testid="p === 'gut' ? 'journal-step-gut' : 'journal-step-landed'" @close="adding = ''" @save="saveStep(s, p)($event)" /></div>
            </template>
          </div>
        </li>
      </ol>
    </template>
  </div>
</template>

<style scoped>
.thv { color: var(--j-ink); }
.thh { padding: 16px 0 6px; }
.krow { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 34px; padding-right: 30px; }
.k { font: 600 10px var(--j-display); letter-spacing: .22em; text-transform: uppercase; color: var(--j-hand); }
.bk { border: 0; background: none; padding: 8px 0; cursor: pointer; font: 600 13px var(--j-ui); color: var(--j-ink2); white-space: nowrap; }
.thh h2 { margin: 4px 0 2px; font: italic 600 34px/1.1 var(--j-serif); color: var(--j-ink); }
.meta { margin: 4px 0 0; font: 500 13px var(--j-ui); color: var(--j-ink3); }
.arc { margin: 12px 0 8px; padding: 10px 12px; border-radius: 10px; background: #ecdfbf; box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .25); font: 500 16px/1.4 var(--j-serif); }
.arc b { font: 600 9.5px var(--j-display); letter-spacing: .16em; text-transform: uppercase; color: var(--j-hand); display: block; }
.steps { list-style: none; margin: 10px 0 0; padding: 0; position: relative; }
.steps::before { content: ''; position: absolute; left: 49px; top: 8px; bottom: 8px; width: 1.3px; background: rgba(140, 102, 40, .45); }
.st { display: grid; grid-template-columns: 42px 1fr; gap: 18px; padding: 10px 0; position: relative; }
.st::before { content: ''; position: absolute; left: 45px; top: 18px; width: 9px; height: 9px; border-radius: 50%; background: var(--j-paper); border: 1.5px solid var(--j-hand); box-sizing: content-box; }
.sd { border: 0; background: none; padding: 4px 0 0; cursor: pointer; font: 600 13px var(--j-display); letter-spacing: .06em; color: var(--j-ink2); text-align: right; align-self: start; }
.sd small { display: block; font: 500 10px var(--j-ui); color: var(--j-ink3); letter-spacing: 0; }
.qrow { display: flex; align-items: flex-start; gap: 2px; }
.q { display: block; flex: 1; min-width: 0; border: 0; background: none; padding: 0; text-align: left; cursor: pointer; font: 500 17px/1.3 var(--j-serif); color: var(--j-ink); }
.q:hover { text-decoration: underline; text-decoration-color: rgba(42, 24, 69, .3); text-underline-offset: 3px; }
.gut, .sh { margin-top: 3px; font: italic 500 16px/1.35 var(--j-serif); color: var(--j-ink2); }
.gut b, .sh b { font: 600 9px var(--j-display); letter-spacing: .16em; text-transform: uppercase; color: var(--j-hand); font-style: normal; margin-right: 6px; }
.hidnote { margin: 14px 0 0; font: italic 500 15px var(--j-serif); color: var(--j-ink3); }
@media (max-width: 899px) {
  .k { letter-spacing: .14em; font-size: 9.5px; }
  .krow { padding-right: 34px; }
  .st { grid-template-columns: 36px 1fr; gap: 16px; }
  .steps::before { left: 42px; }
  .st::before { left: 38px; }
}
</style>
